// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { SohoModalDialogService } from 'ids-enterprise-ng';
import { LMComputeBuilderComponent } from '@modal/compute-builder/lm-compute-builder.component';
import { LMDerivedComputeBuilderComponent } from '@modal/compute-builder/lm-derived-compute-builder.component';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { LMFormRequest } from '@form/index';
import { FormRequestType } from '@app/utils';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';

@Injectable()
export class LMComputeBuilderService {

  constructor(
    private sohoModalDialogService: SohoModalDialogService,
    private http: HttpClient,
    private lmFormatUtilService: LMFormatUtilService) { }

  public openComputeBuilderDialog(busclass: string, fieldLookUpUrl: string, fieldSearchUrl: string,
    lplValidationUrl: string, fieldHasComputeLpl: boolean, isCustomCompute = false, includeAuditFields: boolean, resourceId?: string, customComputeLpl?: string): Observable<LMComputeBuilderResult> {
    let lmComputeBuilderComponentRef: LMComputeBuilderComponent;
    const returnQuery = new Subject<LMComputeBuilderResult>();

    const closeModalWith = (lpl, result, modal) => {
      if (result === 'Ok' && isCustomCompute && lmComputeBuilderComponentRef.hasError()) {
        return;
      }
      returnQuery.next({lplQuery: lpl, action: result});
      modal.close(true);
      returnQuery.complete();
    };

    let buttons = [];

    // Create modal button
    if (fieldHasComputeLpl && !isCustomCompute) {
      buttons = [
        {
          text: Soho.Locale.translate('Cancel'),
          click: (e, modal) => {
            closeModalWith(undefined, 'Cancel', modal);
          }
        },
        {
          text: Soho.Locale.translate('Append'),
          click: (e, modal) => {
            closeModalWith(lmComputeBuilderComponentRef.generatedComputeLpl, 'Append', modal);
          },
          isDefault: true,
        },
        {
          text: Soho.Locale.translate('Overwrite'),
          click: (e, modal) => {
            closeModalWith(lmComputeBuilderComponentRef.generatedComputeLpl, 'Overwrite', modal);
          },
          isDefault: true,
        }
      ];

    } else {
      buttons = [
        {
          text: Soho.Locale.translate('Cancel'),
          click: (e, modal) => {
            closeModalWith(undefined, 'Cancel', modal);
          }
        },
        {
          text: Soho.Locale.translate('Ok'),
          click: (e, modal) => {
            lmComputeBuilderComponentRef.handleValidationEvent();
            closeModalWith(lmComputeBuilderComponentRef.generatedComputeLpl, 'Ok', modal);
          },
          isDefault: true,
        },
      ];
    }

    const attributes: SohoAttributesArray = [{ name: 'data-automation-id', value: `lm-compute-builder-modal` }];

    this.sohoModalDialogService
      .modal(LMComputeBuilderComponent, null, { attributes })
      .title(Soho.Locale.translate('LplComputeBuilderTitle'))
      .buttons(buttons)
      .open()
      .apply((dialogComponent: LMComputeBuilderComponent) => {
        lmComputeBuilderComponentRef = dialogComponent;
        lmComputeBuilderComponentRef.businessClass = busclass;
        lmComputeBuilderComponentRef.lookupUrl = fieldLookUpUrl;
        lmComputeBuilderComponentRef.fieldSearchUrl = fieldSearchUrl;
        lmComputeBuilderComponentRef.lplValidationUrl = lplValidationUrl;
        lmComputeBuilderComponentRef.isCustomCompute = isCustomCompute;
        lmComputeBuilderComponentRef.resourceId = resourceId;
        lmComputeBuilderComponentRef.customComputeLpl = customComputeLpl;
        lmComputeBuilderComponentRef.includeAuditFields = includeAuditFields;
      });

    return returnQuery.asObservable();
  }

  public openDerivedComputeBuilderDialog(busclass: string,
    lplValidationUrl: string, resourceId: string, derivedLpl?: string): Observable<LMComputeBuilderResult> {
    let lmDerivedComputeBuilderComponentRef: LMDerivedComputeBuilderComponent;
    const returnQuery = new Subject<LMComputeBuilderResult>();

    const closeModalWith = (lpl, result, modal) => {
      if (result === 'Ok' && lmDerivedComputeBuilderComponentRef.hasError()) {
        return;
      }
      returnQuery.next({lplQuery: lpl, action: result});
      modal.close(true);
      returnQuery.complete();
    };

    // Create modal button
    const buttons = [
      {
        text: Soho.Locale.translate('Cancel'),
        click: (e, modal) => {
          closeModalWith(undefined, 'Cancel', modal);
        }
      },
      {
        text: Soho.Locale.translate('Ok'),
        click: (e, modal) => {
          lmDerivedComputeBuilderComponentRef.handleValidationEvent();
          closeModalWith(lmDerivedComputeBuilderComponentRef.preview, 'Ok', modal);
        },
        isDefault: true,
      },
    ];

    const attributes: SohoAttributesArray = [{ name: 'data-automation-id', value: `lm-derived-compute-builder-modal` }];

    this.sohoModalDialogService
      .modal(LMDerivedComputeBuilderComponent, null, <SohoModalOptions>{ attributes })
      .title(Soho.Locale.translate('LplDerivedBuilderTitle'))
      .buttons(buttons)
      .open()
      .apply((dialogComponent: LMDerivedComputeBuilderComponent) => {
        lmDerivedComputeBuilderComponentRef = dialogComponent;
        lmDerivedComputeBuilderComponentRef.businessClass = busclass;
        lmDerivedComputeBuilderComponentRef.lplValidationUrl = lplValidationUrl;
        lmDerivedComputeBuilderComponentRef.resourceId = resourceId;
        lmDerivedComputeBuilderComponentRef.derivedLpl = derivedLpl;
      });

    return returnQuery.asObservable();
  }

  public convertComputeRowCriteria(computeRows: Array<LMComputeRow>, customCompute?: LMCustomCompute): string {
    let query = ``;
    if (customCompute && ( customCompute.fieldName || customCompute.fieldType)) {
      query = `${customCompute.fieldName} is a ComputeField\n`;
      if (customCompute.fieldType === 'Period'
        || customCompute.fieldType === 'Time'
        ||  customCompute.fieldType === 'Date') {
        query += `\ttype is ${customCompute.fieldType}\n`;
      } else if (customCompute.fieldType) {
        query += `\ttype is ${customCompute.fieldType} size ${customCompute.size}${customCompute.decimal ? `.${customCompute.decimal}` : ''}\n`;
      }

      query += `\t`;
    }

    if (!this.emptyRows(computeRows)) {
      query += '(';
      computeRows.forEach((computeRow, index, array) => {

        let field = '';
        if (computeRow.type === 'string') {
          field = `"${computeRow.field.text}"`;
        } else {
          field = `${computeRow.field.text}`
        }

        if (index === (array.length - 1)) {
          if (computeRow.function) {
            if (computeRow.position === 'append') {
              query += `${field} ${computeRow.function}`;
            } else if (computeRow.position === 'prepend') {
              query += `${computeRow.function} ${field}`;
            }
          } else {
            query += `${field}`;
          }
        } else {
          if (computeRow.function) {
            if (computeRow.position === 'append') {
              query += `${field} ${computeRow.function} `;
            } else if (computeRow.position === 'prepend') {
              query += `${computeRow.function} ${field} `;
            }
          } else {
            query += `${field} `;
          }
        }

        if (computeRow.operator) {
          query += `${computeRow.operator} `;
        }
      });
      query += ')';
    }
    return query;
  }

  public convertDerivedComputeCriteria(customCompute: LMCustomCompute): string {
    let query = ``;
    if (customCompute) {
      query = `${customCompute.fieldName} is a DerivedField\n`;
      if (customCompute.fieldType === 'Period'
        || customCompute.fieldType === 'Time'
        ||  customCompute.fieldType === 'Date') {
        query += `\ttype is ${customCompute.fieldType}\n`;
      } else {
        query += `\ttype is ${customCompute.fieldType} size ${customCompute.size}${customCompute.decimal ? `.${customCompute.decimal}` : ''}\n`;
      }

      if (customCompute.computeLpl) {
        const lines = customCompute.computeLpl.split('\n');
        lines.forEach(value => {
          query += `\t${value}\n`;
        });
      }
    }

    return query;
  }

  private emptyRows(computeRows: Array<LMComputeRow>): boolean {
    return (computeRows.filter( computeRow => (computeRow.field.text !== '')).length === 0)
  }

  public parseDerivedLpl(derivedLpl: string): LMCustomCompute {
    const customCompute: LMCustomCompute = {
      fieldName: '',
      fieldType: '',
      size: '',
      decimal: '',
      computeLpl: ''
    };

    const lpl = derivedLpl.split('\n');
    lpl.forEach(value => {

      if (value.includes(' is a DerivedField')) {
        customCompute.fieldName = value.substring(0, value.indexOf(' is a DerivedField')).trim();
      }

      if (value.includes('type is ')) {
        if (value.includes(' size')) {
          customCompute.fieldType = value.substring(value.indexOf('type is ') + 8, value.indexOf(' size'));

          const size = value.substring(value.indexOf(' size ') + 6, value.indexOf('.') !== -1 ? value.indexOf('.') + 2 : value.length);

          if (size.indexOf('.') !== -1) {
            customCompute.size = size.split('.')[0];
            customCompute.decimal = size.split('.')[1];
          } else {
            customCompute.size = size.trim();
          }

        } else {
          customCompute.fieldType = value.substring(value.indexOf('type is ') + 8, value.length).trim();
        }
      }

      if (!value.includes(' is a DerivedField') && !value.includes('type is ')) { // Condition
        if (value.startsWith('\t\t')) {
          customCompute.computeLpl += value.substring(2, value.length) + '\n'; // to remove the starting tabs
        } else if (value.startsWith('\t')) {
          customCompute.computeLpl += value.substring(1, value.length) + '\n'; // to remove the starting tabs
        } else if (value.startsWith('    ')) {
          customCompute.computeLpl += value.substring(4, value.length) + '\n'; // to remove the starting spaces
        } else {
          customCompute.computeLpl += value + '\n';
        }
      }
    });

    return customCompute;
  }

  public parseCustomComputeLpl(customComputeLpl: string,  businessClass: string, fieldSearchUrl: string): LMCustomCompute {
    const customCompute: LMCustomCompute = {
      fieldName: '',
      fieldType: '',
      size: '',
      decimal: ''
    };

    const lpl = customComputeLpl.split('\n');
    lpl.forEach(value => {

      if (value.includes(' is a ComputeField')) {
        customCompute.fieldName = value.substring(0, value.indexOf(' is a ComputeField')).trim();
      }

      if (value.includes('type is ')) {
        if (value.includes(' size')) {
          customCompute.fieldType = value.substring(value.indexOf('type is ') + 8, value.indexOf(' size'));

          const size = value.substring(value.indexOf(' size ') + 6, value.indexOf('.') !== -1 ? value.indexOf('.') + 2 : value.length);

          if (size.indexOf('.') !== -1) {
            customCompute.size = size.split('.')[0];
            customCompute.decimal = size.split('.')[1];
          } else {
            customCompute.size = size.trim();
          }

        } else {
          customCompute.fieldType = value.substring(value.indexOf('type is ') + 8, value.length).trim();
        }
      }

    });
    return customCompute;
  }

  public computeIsValid(lplValidationUrl: string, busClass: string, lpl: string, computeType: string): Observable<LMLplValidationResponse> {
    const formRequest: LMFormRequest = {
      requestType: FormRequestType.ValidateCondition,
      param1: busClass,
      param2: lpl,
      isBusinessClassCompute: true,
      computeType
    };

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return <Observable<LMLplValidationResponse>>  this.http
      .post(lplValidationUrl, formRequest);
  }

  public validateFieldName(fieldName: string): string {
    const firstChar = fieldName.charAt(0);
    const isUpperCase = /^[A-Z]*$/.test(firstChar);
    const invalidChar = this.checkInvalidCharacter(fieldName);

    if (this.hasUnicodeCharacters(fieldName)) {
      return Soho.Locale.translate('FIELD_CONTAINS_INVALID_LPL_CHARACTER');
    }

    if (/^[A-Za-z]+$/.test(firstChar) && !isUpperCase) {
      return Soho.Locale.translate('FIELD_LC_ERROR_MESSAGE');
    }

    if (fieldName.indexOf(' ') >= 0) {
      return Soho.Locale.translate('FIELD_SPACE_CHAR_ERROR_MESSAGE');
    }

    if (invalidChar !== '') {
      return this.lmFormatUtilService.formatMessage(Soho.Locale.translate('FIELD_INVALID_CHAR_ERROR_MESSAGE'), invalidChar);
    }

    return '';
  }

  private hasUnicodeCharacters(fieldName: string): boolean {
    for (let i = 0; i < fieldName.length; i++) {
      if (fieldName.charCodeAt( i ) > 127) {
        return true;
      }
    }
    return false;
  }

  private checkInvalidCharacter(fieldName: string): string {
    const invalidChar = fieldName.match(/[^\w]|_/);
    if (invalidChar) {
      return invalidChar[0];
    }
    return '';
  }
}
